import { useState } from "react";
import { Check, Copy, Printer } from "lucide-react";

import { useMutation } from "@tanstack/react-query";

import { ApiError, formatDateTime } from "../../lib/api";
import { fechaDMA } from "../../lib/fechaLocal";
import { useToast } from "../../components/Toast";
import { Button } from "../../components/ui";
import {
  CANAL_LABELS,
  TIPO_LABELS,
  descargarComprobante,
  folioAtencion,
  type Atencion,
} from "./api";

export function textoPlazo(a: Atencion): string {
  if (a.tipo === "Emergencia") return "El mismo día";
  return a.fecha_limite_respuesta
    ? `Hasta el ${fechaDMA(a.fecha_limite_respuesta)}`
    : "20 días hábiles";
}

/** Texto plano del comprobante, para copiar a WhatsApp o correo. */
export function textoComprobante(a: Atencion): string {
  return [
    `Atención N° ${folioAtencion(a)}`,
    `Ingreso: ${formatDateTime(a.fecha_ingreso)}`,
    `Tipo: ${TIPO_LABELS[a.tipo]}`,
    `A nombre de: ${a.usuario_nombre}`,
    `Plazo de respuesta: ${textoPlazo(a)}`,
  ].join("\n");
}

/**
 * Comprobante que se entrega al usuario al registrar su atención: número,
 * fecha de ingreso, tipo y plazo de respuesta. Antes el número sólo salía
 * en un aviso que desaparecía a los segundos.
 */
export function ComprobanteAtencion({
  atencion,
  onListo,
}: {
  atencion: Atencion;
  onListo: () => void;
}): JSX.Element {
  const toast = useToast();
  const [copiado, setCopiado] = useState(false);

  // El PDF lo arma el servidor (una hoja, con nombre y RUT del comité):
  // imprimir el modal desde el navegador salía en varias hojas.
  const imprimir = useMutation({
    mutationFn: () => descargarComprobante(atencion),
    onError: (err: unknown) =>
      toast.error(
        err instanceof ApiError ? err.message : "No se pudo descargar el comprobante",
      ),
  });

  async function copiar(): Promise<void> {
    try {
      await navigator.clipboard.writeText(textoComprobante(atencion));
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 2000);
    } catch {
      setCopiado(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-neutral-200 bg-white p-4 text-sm text-neutral-800"
      >
        <div className="text-xs font-medium uppercase tracking-wider text-neutral-600">
          Comprobante de atención
        </div>
        <div className="mt-1 text-2xl font-semibold tabular-nums text-neutral-900">
          N° {folioAtencion(atencion)}
        </div>
        <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
          <Dato label="Fecha de ingreso" valor={formatDateTime(atencion.fecha_ingreso)} />
          <Dato label="Tipo" valor={TIPO_LABELS[atencion.tipo]} />
          <Dato label="Canal" valor={CANAL_LABELS[atencion.canal_recepcion]} />
          <Dato label="A nombre de" valor={atencion.usuario_nombre} />
          <Dato label="Plazo de respuesta" valor={textoPlazo(atencion)} destacado />
        </dl>
        <div className="mt-3">
          <div className="text-xs font-medium text-neutral-600">Motivo</div>
          <p className="whitespace-pre-wrap">{atencion.descripcion}</p>
        </div>
        <p className="mt-3 text-xs text-neutral-600">
          Con este número puedes consultar el estado de tu atención en la oficina
          del comité.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="secondary" icon={copiado ? Check : Copy} onClick={() => void copiar()}>
          {copiado ? "Copiado" : "Copiar"}
        </Button>
        <Button
          variant="secondary"
          icon={Printer}
          onClick={() => imprimir.mutate()}
          disabled={imprimir.isPending}
          title="Descarga el comprobante en PDF, listo para imprimir"
        >
          {imprimir.isPending ? "Generando…" : "Imprimir"}
        </Button>
        <Button className="ml-auto" onClick={onListo}>
          Listo
        </Button>
      </div>
      <span className="sr-only" aria-live="polite">
        {copiado ? "Comprobante copiado" : ""}
      </span>
    </div>
  );
}

function Dato({
  label,
  valor,
  destacado,
}: {
  label: string;
  valor: string;
  destacado?: boolean;
}): JSX.Element {
  return (
    <div>
      <dt className="text-xs font-medium text-neutral-600">{label}</dt>
      <dd className={destacado ? "font-semibold text-brand-700" : undefined}>{valor}</dd>
    </div>
  );
}
